import { useEffect } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  EquipmentCategory,
  EquipmentCategoryParams,
} from "@/types/equipment";

// 类目表单模式：新增根类目、新增子类目或编辑类目
export type CategoryFormMode = "root" | "child" | "edit";

// 类目表单组件参数
interface CategoryFormProps {
  open: boolean; // 弹窗是否打开
  mode: CategoryFormMode; // 当前表单模式
  category?: EquipmentCategory; // 新增子类目时的父类目或编辑时的类目详情
  confirmLoading: boolean; // 提交状态
  onCancel: () => void; // 关闭表单
  onSubmit: (body: EquipmentCategoryParams) => Promise<void>; // 提交类目数据
}

// 单个类目的可编辑字段
type EquipmentCategoryFormItem = Pick<
  EquipmentCategoryParams,
  "code" | "name" | "desc"
>;

// 类目表单值，支持同时维护多个子类目
interface EquipmentCategoryFormValues extends EquipmentCategoryFormItem {
  subs: EquipmentCategoryFormItem[]; // 批量新增或编辑的子类目
}

// 生成新增子类目的空白表单项
const emptyCategory = (): EquipmentCategoryFormItem => ({
  code: "",
  name: "",
  desc: null,
});

// 将可选描述统一转换为接口接受的 null
const normalizeCategory = (
  category: EquipmentCategoryFormItem,
): EquipmentCategoryFormItem => ({
  code: category.code,
  name: category.name,
  desc: category.desc || null,
});

// 新增或编辑设备类目，并支持批量维护直接子类目
const CategoryForm = ({
  open,
  mode,
  category,
  confirmLoading,
  onCancel,
  onSubmit,
}: CategoryFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<EquipmentCategoryFormValues>();
  const parentCode = mode === "child" ? category?.code ?? "" : "";  // 父类目编号，新增子类目时使用

  useEffect(() => {
    // 弹窗关闭时清理上一次填写内容
    if (!open) {
      form.resetFields();
      return;
    }

    // 编辑时回填类目及其子类目，新增时使用空表单
    form.resetFields();
    form.setFieldsValue(
      mode === "edit" && category
        ? {
          code: category.code,
          name: category.name,
          desc: category.desc,
          subs: (category.subs ?? []).map(normalizeCategory),
        }
        : {
          code: "",
          name: "",
          desc: null,
          subs: [],
        },
    );
  }, [category, form, mode, open]);

  const title = mode === "root"
    ? t("equipment.addRootCategory")
    : mode === "child"
      ? t("equipment.addSubCategory")
      : t("equipment.editCategory");

  const handleSubmit = async () => {
    let values: EquipmentCategoryFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // 提交前统一整理主类目和子类目的可选字段
    const body: EquipmentCategoryParams = {
      ...normalizeCategory({
        code: `${parentCode}${values.code}`,
        name: values.name,
        desc: values.desc,
      }),
      subs: (values.subs ?? []).map(normalizeCategory),
    };

    await onSubmit(body);
  };

  return (
    <Modal
      title={title}
      open={open}
      confirmLoading={confirmLoading}
      maskClosable={false}
      destroyOnClose
      width={760}
      styles={{
        content: { padding: "16px 20px" },
        header: { marginBottom: 8 },
        footer: { marginTop: 8 },
      }}
      okText={t("common.confirm")}
      cancelText={t("common.cancel")}
      onCancel={onCancel}
      onOk={handleSubmit}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}
      >
        <Form.Item
          label={t("equipment.categoryCode")}
          name="code"
          rules={[{
            required: true,
            whitespace: true,
            message: t("equipment.categoryCodeRequired"),
          }]}
          style={{ marginBottom: 12 }}
        >
          <Input addonBefore={parentCode || undefined} />
        </Form.Item>
        <Form.Item
          label={t("equipment.categoryName")}
          name="name"
          rules={[{
            required: true,
            whitespace: true,
            message: t("equipment.categoryNameRequired"),
          }]}
          style={{ marginBottom: 12 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t("equipment.categoryDesc")}
          name="desc"
          style={{ marginBottom: 12 }}
        >
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
        </Form.Item>
        <Form.Item
          label={t("equipment.batchSubCategories")}
          style={{ marginBottom: 0 }}
        >
          <Form.List name="subs">
            {(fields, { add, remove }) => (
              <Flex vertical gap={12}>
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    size="small"
                    title={t("equipment.subCategoryItem", {
                      index: index + 1,
                    })}
                    extra={(
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t("equipment.removeSubCategory")}
                        onClick={() => remove(field.name)}
                      />
                    )}
                  >
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          label={t("equipment.categoryCode")}
                          name={[field.name, "code"]}
                          rules={[{
                            required: true,
                            whitespace: true,
                            message: t("equipment.categoryCodeRequired"),
                          }]}
                          style={{ marginBottom: 12 }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={t("equipment.categoryName")}
                          name={[field.name, "name"]}
                          rules={[{
                            required: true,
                            whitespace: true,
                            message: t("equipment.categoryNameRequired"),
                          }]}
                          style={{ marginBottom: 12 }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          label={t("equipment.categoryDesc")}
                          name={[field.name, "desc"]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  block
                  onClick={() => add(emptyCategory())}
                >
                  {t("equipment.addBatchSubCategory")}
                </Button>
              </Flex>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CategoryForm;
